import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

export default function CSS_ReferenceAtRules() {
  const sectionStyle = {
    padding: "1rem",
    paddingLeft: "35px",
    borderRadius: "12px",
    marginBottom: "2rem",
  };

  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">🔊 CSS Aural (Speech) Properties</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        CSS Aural properties (also called <b>Speech CSS</b>) are used to control
        how web content is spoken by speech synthesizers. These properties were
        part of the <b>CSS2 Speech module</b> and are useful for accessibility
        and screen reader customization.
      </p>

      {/* Key Points */}
      <div
        className="p-4 rounded-2xl mb-6"
        style={{
          ...sectionStyle,
          background: "linear-gradient(90deg, #fce7f3, #fbcfe8)",
          color: "#4a044e",
        }}
      >
        <h2>🔑 Key Points</h2>
        <ul>
          <li>
            Used with the <code>@media speech</code> rule for audio rendering.
          </li>
          <li>Enhances accessibility for visually impaired users.</li>
          <li>
            Controls how text is spoken — pitch, rate, volume, pauses, etc.
          </li>
          <li>
            Not supported by all browsers, mostly used in specialized systems.
          </li>
        </ul>
      </div>

      {/* Properties Table */}
      <div
        className="p-4 rounded-2xl mb-6 overflow-x-auto"
        style={{
          backgroundColor: "#f0fdfa",
          color: "#064e3b",
          borderRadius: "10px",
          paddingTop: "10px",
        }}
      >
        <h2 style={{ padding: "0 0 10px 10px" }}>🎛️ CSS Aural Properties</h2>
        <table
          className="tutorial-table"
          style={{
            width: "100%",
            borderCollapse: "collapse",
            borderRadius: "12px",
            overflow: "hidden",
          }}
        >
          <thead>
            <tr
              style={{
                backgroundColor: "#0f766e",
                color: "#ffffff",
                textAlign: "left",
              }}
            >
              <th style={{ padding: "12px" }}>Property</th>
              <th style={{ padding: "12px" }}>Description</th>
              <th style={{ padding: "12px" }}>Values</th>
            </tr>
          </thead>
          <tbody>
            {[
              [
                "azimuth",
                "Sets where the sound should come from",
                "angle, left-side, far-left, left, center-left, center, center-right, right, far-right, right-side, behind, leftwards, rightwards",
              ],
              [
                "cue",
                "Sets cue sounds in one declaration",
                "cue-before, cue-after",
              ],
              [
                "cue-before / cue-after",
                "Sound before/after speaking content",
                "none, url",
              ],
              [
                "elevation",
                "Sets vertical sound direction",
                "angle, below, level, above, higher, lower",
              ],
              [
                "pause",
                "Sets pauses in one declaration",
                "pause-before, pause-after",
              ],
              [
                "pause-before / pause-after",
                "Pause before or after speech",
                "time, %",
              ],
              [
                "pitch",
                "Specifies the speaking voice pitch",
                "frequency, x-low, low, medium, high, x-high",
              ],
              [
                "pitch-range",
                "Variation in pitch (animated or monotone)",
                "number",
              ],
              [
                "play-during",
                "Background sound while speaking",
                "auto, none, url, mix, repeat",
              ],
              ["richness", "Specifies richness of voice", "number"],
              [
                "speak",
                "Whether the content should be spoken",
                "normal, none, spell-out",
              ],
              ["speak-header", "How to handle table headers", "always, once"],
              ["speak-numeral", "How to speak numbers", "digits, continuous"],
              ["speak-punctuation", "How to handle punctuation", "none, code"],
              [
                "speech-rate",
                "Speed of speaking",
                "number, x-slow, slow, medium, fast, x-fast, faster, slower",
              ],
              ["stress", "Specifies emphasis in the voice", "number"],
              [
                "voice-family",
                "Which voice to use",
                "specific-voice, generic-voice",
              ],
              [
                "volume",
                "Specifies loudness of the voice",
                "number, %, silent, x-soft, soft, medium, loud, x-loud",
              ],
            ].map((row, index) => (
              <tr
                key={index}
                style={{
                  backgroundColor: index % 2 === 0 ? "#ecfdf5" : "#d1fae5",
                  transition: "all 0.3s ease",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "#a7f3d0")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor =
                    index % 2 === 0 ? "#ecfdf5" : "#d1fae5")
                }
              >
                <td style={{ padding: "12px", fontWeight: "bold" }}>
                  {row[0]}
                </td>
                <td style={{ padding: "12px" }}>{row[1]}</td>
                <td style={{ padding: "12px" }}>{row[2]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Example */}
      <div
        className="p-4 rounded-2xl mb-6"
        style={{
          background: "linear-gradient(90deg, #dbeafe, #e0f2fe)",
          marginTop: "50px",
          color: "#1e3a8a",
          padding: "30px",
          borderRadius: "10px",
          marginBottom: "50px",
        }}
      >
        <h2>🧠 Example</h2>
        <CodeCopyBlock
          language="css"
          code={`@media speech {
  h1 {
    voice-family: male;
    volume: loud;
    pitch: high;
    speak: normal;
  }

  p {
    pause-before: 300ms;
    pause-after: 200ms;
    speech-rate: medium;
  }
}`}
        />
        <p>
          This example defines how headers and paragraphs should sound when read
          aloud by a speech device.
        </p>
      </div>

      {/* Next Steps */}
      <div
        className="p-4 rounded-2xl"
        style={{
          ...sectionStyle,
          background: "linear-gradient(90deg, #fef9c3, #fef08a)",
          color: "#713f12",
        }}
      >
        <h2>🚀 Next Steps</h2>
        <ul>
          <li>
            Experiment with <code>@media speech</code> and try various
            pitch/rate combinations.
          </li>
          <li>
            Use Aural properties with assistive technology for accessibility
            testing.
          </li>
          <li>
            Combine with ARIA attributes for better voice rendering in screen
            readers.
          </li>
        </ul>
      </div>
    </div>
  );
}
